import React, { useState, useEffect } from "react";
import {
  Drawer,
  Box,
  Typography,
  Tabs,
  Tab,
  Container,
  IconButton,
  Grid,
} from "@mui/material";
import { getInfoData } from "../../../api/simpleSearchApi/simpleSearchApi";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import Loading from "../../Alert/Loading";
import useNotification from "../../Hooks/useNotification";
import useLoading from "../../Hooks/useLoading";
import isPagePermission from "../../../utils/common";
import { useLocation } from "react-router-dom";
import LocalStorageUtil from "../../../utils/localStorage";
import { FormatDateVersionHistory, FormatDate } from "../../Common/CommonMethods";

interface CustomDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  title: string; //NOSONAR
  tabLabels: string[];
  selectedDocument: any;
  screenType?: string;
}

const InfoDrawer: React.FC<CustomDrawerProps> = ({
  isOpen,
  onClose,
  tabLabels,
  selectedDocument,
  screenType,
}) => {
  const [value, setValue] = useState<number>(0);
  const [generalInformation, setGeneralInformation] = useState<any>(null);
  const [versionHistory, setVersionHistory] = useState<any>(null);
  const { showFailure } = useNotification();
  const { loading, startLoading, stopLoading } = useLoading();
  const location = useLocation();
  const currentPath = location.pathname;

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const fetchInfoData = async (): Promise<any> => {
    let infoPayload = {};
    startLoading();
    if (screenType === "Documents") {
      infoPayload = {
        AccountId: LocalStorageUtil.getItem("accountId"),
        QueueTypeId: selectedDocument[0].QueueType,
        UserId: LocalStorageUtil.getItem("userId"),
        contextName: LocalStorageUtil.getItem("contextName")?.toString(),
        documentId: selectedDocument[0].documentID,
      };
    } else if (screenType === "TCMStatusSearch") {
      infoPayload = {
        AccountId: LocalStorageUtil.getItem("accountId"),
        QueueTypeId: selectedDocument.QueueType,
        UserId: LocalStorageUtil.getItem("userId"),
        contextName: LocalStorageUtil.getItem("contextName")?.toString(),
        documentId: selectedDocument.documentStreamID,
        projectDocumentId: selectedDocument.documentStreamID,
      };
    } else {
      infoPayload = {
        AccountId: LocalStorageUtil.getItem("accountId"),
        QueueTypeId: selectedDocument.QueueType,
        UserId: LocalStorageUtil.getItem("userId"),
        contextName: LocalStorageUtil.getItem("contextName")?.toString(),
        documentId: selectedDocument.DocumentID,
        projectDocumentId:
          selectedDocument.DocumentStreamID ??
          selectedDocument.childFileDocumentStreamID,
        rendition: selectedDocument.DocumentStatusValue,
      };
    }

    try {
      const response = await getInfoData(infoPayload);
      setGeneralInformation(response?.generalInformation || []);
      setVersionHistory(response?.visionHistory || []);
    } catch (error) {
      showFailure("Something went wrong");
    } finally {
      stopLoading();
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchInfoData();
    }
  }, [isOpen]);

  const renderGenralInformation = (
    generalInformation: Array<{
      groupName: string;
      group: Record<string, any>;
    }> | null,
  ) => {
    if (!generalInformation || generalInformation.length === 0) {
      return <Typography variant="body1">No information available</Typography>;
    }

    return generalInformation.map((section, index) => (
      <Box key={section.groupName} sx={{ mt: 2 }}>
        {section.groupName && (
          <div
            style={{
              color: "#325680",
              textTransform: "uppercase",
              fontSize: "17px",
              fontWeight: "bold",
              borderBottom: "1px dashed #325680",
              paddingBottom: "5px",
              marginBottom: "5px",
            }}
          >
            {section.groupName}
          </div>
        )}
        <Grid container spacing={1}>
          {Object.entries(section.group).map(([label, value]) => (
            <Grid item xs={12} key={label}>
              <div
                style={{
                  fontFamily: "Verdana, Arial, Times New Roman, sans-serif",
                  fontSize: "0.7em",
                  fontWeight: "bold",
                  color: "#006ab6",
                }}
              >
                {value.fields}
              </div>
              <div style={{ fontSize: "0.7em" }} color="textSecondary">
                {value.values ? 
                  (value.fields?.toLowerCase().includes('date') ? 
                    FormatDate(value.values) : 
                    value.values) : 
                  "-"
                }
              </div>
            </Grid>
          ))}
        </Grid>
      </Box>
    ));
  };

  const renderVisionHistory = (
    visionHistory: Array<{
      level1: string;
      activityName: string;
      description: string;
      userName: string;
      activityDateTime: string;
      locationName: string;
    }> | null,
  ) => {
    if (!visionHistory || visionHistory.length === 0) {
      return (
        <Typography variant="body1">No vision history available</Typography>
      );
    }

    return (
      <ul
        style={{
          fontFamily: "Verdana, Arial, Times New Roman, sans-serif",
          paddingLeft: ".5em",
          marginTop: "0.5em",
        }}
      >
        {visionHistory.map((item) => (
          <li
            key={item.activityDateTime + item.userName}
            style={{ fontSize: "1em" }}
          >
            <div
              style={{
                fontSize: "0.7em",
              }}
            >
              <p>
                {item.level1 !== undefined && item.level1 !== null && (
                  <>
                    <span>{item.level1}</span>
                    <br />
                  </>
                )}
                <b>
                  {item.activityName} : {item.description}
                  <br />
                </b>
                {item.userName}
                <br />
                <b> On </b>
                <span>{FormatDateVersionHistory(item.activityDateTime)}</span>

                {item.locationName !== null && item.locationName !== "" && (
                  <>
                    <br />
                    <b>Location:</b>
                    <span>{item.locationName}</span>
                  </>
                )}
              </p>
            </div>
          </li>
        ))}
      </ul>
    );
  };
  const renderContent = () => {
    if (value === 0) {
      if (loading) return <Typography variant="body1">Loading...</Typography>;
      if (generalInformation)
        return renderGenralInformation(generalInformation);
      return null;
    }

    if (versionHistory) return renderVisionHistory(versionHistory);

    return null;
  };

  // Helper function to define version history permission mappings
  const getVersionHistoryPermissions = (): Record<
    string,
    { screen: string; permissionId: number }
  > => {
    return {
      "/advancedSearch": { screen: "AdvanceSearch", permissionId: 9 },
      "/listsearch": { screen: "ListSearch", permissionId: 1348 },
      "/simpleSearch": { screen: "Search", permissionId: 803 },
      "/archivalreport": { screen: "ArchivalReport", permissionId: 1442 },
      "/preworkdetails": { screen: "PreworkDetail", permissionId: 1345 },
      "/downstream": { screen: "Downstream", permissionId: 208 },
      "/documents": { screen: "Documents", permissionId: 253 },
    };
  };

  // Helper function to check if version history is allowed for current path
  const isVersionHistoryAllowed = (currentPath: string): boolean => {
    const permissions = getVersionHistoryPermissions();
    const pathConfig = permissions[currentPath];

    if (!pathConfig) {
      return false;
    }

    return isPagePermission(
      "Allow Version History",
      pathConfig.screen,
      pathConfig.permissionId,
    );
  };

  // Helper function to filter visible tabs
  const filterVisibleTabs = (
    tabLabels: string[],
    currentPath: string,
  ): string[] => {
    return tabLabels.filter((label) => {
      if (label === "Information") {
        return true;
      }

      if (label === "Version History") {
        return isVersionHistoryAllowed(currentPath);
      }

      return false;
    });
  };

  // Refactored visibleTabs calculation
  const visibleTabs = filterVisibleTabs(tabLabels, currentPath);
  return (
    <div>
      <Loading show={loading} />

      <Drawer
        anchor="right"
        open={isOpen}
        onClose={onClose}
        hideBackdrop={true}
        ModalProps={{
          keepMounted: true,
          disableEnforceFocus: true,
          BackdropProps: {
            invisible: true,
          },
        }}
        sx={{
          pointerEvents: "none", // Let clicks pass through Drawer container
          "& .MuiDrawer-paper": {
            pointerEvents: "auto", // But still allow interaction with the Drawer content itself
          },
        }}
      >
        <Box sx={{ width: 300, padding: 2, position: "relative" }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6"></Typography>
            <IconButton onClick={onClose}>
              <ArrowForwardIcon />
            </IconButton>
          </Box>
          <Tabs
            value={value}
            onChange={handleTabChange}
            sx={{ mt: 2 }}
            TabIndicatorProps={{
              sx: {
                background: "blue",
              },
            }}
            style={{ textTransform: "capitalize" }}
          >
            {visibleTabs.map((label) => (
              <Tab key={label} label={label} />
            ))}
          </Tabs>
          <Container>{renderContent()}</Container>
        </Box>
      </Drawer>
    </div>
  );
};

export default InfoDrawer;
